import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { calculateTrialCost } from '@/utils/trialCostCalculator';

export interface ClassTrial {
  id: string;
  class_date: string | null;
  class_type: string | null;
  cost: number;
  grade: string | null;
  location: string | null;
  month: number;
  note: string | null;
  status: string | null;
  student_name: string | null;
  student_nickname: string | null;
  student_firstname: string | null;
  student_lastname: string | null;
  teacher_code: string | null;
  year: number;
  order_code: string | null;
  hours: number;
  parent_phone: string | null;
  student_phone: string | null;
  subject: string | null;
  trial_time: string | null;
  actual_trial_day: string | null;
  actual_trial_time: string | null;
  desired_course: string | null;
  parent_expectations_trial: string | null;
  language: string | null;
  classroom_type: string | null;
  learning_format: string | null;
}

export interface ClassTrialFormData {
  class_date: string;
  class_type: string;
  grade: string;
  location: string;
  status: string;
  student_name: string;
  student_nickname: string;
  teacher_code: string;
  note: string;
  order_code: string | null;
  hours: number;
}

// Get billing period date range (26 of prev month to 25 of current month)
// Returns date strings directly to avoid timezone issues
export function getBillingPeriodRange(month: number, year: number) {
  // For month M, year Y: 26th of (M-1) to 25th of M
  const startMonth = month === 1 ? 12 : month - 1;
  const startYear = month === 1 ? year - 1 : year;
  
  // Format date strings directly without timezone conversion
  const startDate = `${startYear}-${String(startMonth).padStart(2, '0')}-26`;
  const endDate = `${year}-${String(month).padStart(2, '0')}-25`;
  
  return { startDate, endDate };
}

// Get month/year from a class date based on billing period logic
export function getMonthYearFromDate(classDate: string): { month: number; year: number } {
  const date = new Date(classDate);
  const day = date.getDate();
  let month = date.getMonth() + 1; // 1-12
  let year = date.getFullYear();
  
  // If day >= 26, it belongs to next month's billing period
  if (day >= 26) {
    month = month + 1;
    if (month > 12) {
      month = 1;
      year = year + 1;
    }
  }
  
  return { month, year };
}

export function useClassTrials() {
  const [trials, setTrials] = useState<ClassTrial[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTrials = useCallback(async (startDate: string, endDate: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('class_trials')
        .select('*')
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('class_date', { ascending: false });

      if (error) throw error;
      setTrials(data || []);
    } catch (error) {
      console.error('Error fetching trials:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถโหลดข้อมูลทดลองเรียนได้',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createTrial = useCallback(async (data: ClassTrialFormData, teacherTier: string | null) => {
    try {
      const hasCommission = data.status === 'ปิดการขายเรียบร้อย';
      const cost = calculateTrialCost(data.grade, data.class_type, data.location, hasCommission, teacherTier, data.hours);
      const { month, year } = getMonthYearFromDate(data.class_date);

      const { error } = await supabase
        .from('class_trials')
        .insert({
          class_date: data.class_date,
          class_type: data.class_type,
          grade: data.grade,
          location: data.location,
          status: data.status,
          student_name: data.student_name,
          student_nickname: data.student_nickname,
          teacher_code: data.teacher_code,
          note: data.note || '',
          order_code: data.order_code,
          hours: data.hours,
          cost,
          month,
          year,
        } as any);

      if (error) throw error;
      
      toast({
        title: 'สำเร็จ',
        description: 'เพิ่มข้อมูลทดลองเรียนเรียบร้อยแล้ว',
      });
      return true;
    } catch (error) {
      console.error('Error creating trial:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเพิ่มข้อมูลทดลองเรียนได้',
        variant: 'destructive',
      });
      return false;
    }
  }, []);

  const updateTrial = useCallback(async (id: string, data: ClassTrialFormData, teacherTier: string | null) => {
    try {
      const hasCommission = data.status === 'ปิดการขายเรียบร้อย';
      const cost = calculateTrialCost(data.grade, data.class_type, data.location, hasCommission, teacherTier, data.hours);
      const { month, year } = getMonthYearFromDate(data.class_date);

      const { error } = await supabase
        .from('class_trials')
        .update({
          class_date: data.class_date,
          class_type: data.class_type,
          grade: data.grade,
          location: data.location,
          status: data.status,
          student_name: data.student_name,
          student_nickname: data.student_nickname,
          teacher_code: data.teacher_code,
          note: data.note || '',
          order_code: data.order_code,
          hours: data.hours,
          cost,
          month,
          year,
        } as any)
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: 'สำเร็จ',
        description: 'แก้ไขข้อมูลทดลองเรียนเรียบร้อยแล้ว',
      });
      return true;
    } catch (error) {
      console.error('Error updating trial:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถแก้ไขข้อมูลทดลองเรียนได้',
        variant: 'destructive',
      });
      return false;
    }
  }, []);

  const deleteTrial = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('class_trials')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: 'สำเร็จ',
        description: 'ลบข้อมูลทดลองเรียนเรียบร้อยแล้ว',
      });
      return true;
    } catch (error) {
      console.error('Error deleting trial:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถลบข้อมูลทดลองเรียนได้',
        variant: 'destructive',
      });
      return false;
    }
  }, []);

  const updateTrialKanbanStatus = useCallback(async (
    trial: ClassTrial,
    newStatus: string,
    orderInfo?: { orderId: string; orderCode: string } | null,
  ) => {
    if ((trial.status || '') === newStatus) return true;
    try {
      let tier: string | null = null;
      if (trial.teacher_code) {
        const teacherClient = supabase.schema('teacher' as any);
        const { data: teacherRow } = await (teacherClient as any)
          .from('teachers')
          .select('tier')
          .eq('code', trial.teacher_code)
          .maybeSingle();
        tier = teacherRow?.tier ?? null;
      }

      const hasCommission = newStatus === 'ปิดการขายเรียบร้อย';
      const cost = calculateTrialCost(
        trial.grade,
        trial.class_type,
        trial.location,
        hasCommission,
        tier,
        trial.hours,
      );

      const updatePayload: any = { status: newStatus, cost };
      if (orderInfo) {
        updatePayload.order_code = orderInfo.orderCode;
        updatePayload.order_id = orderInfo.orderId;
      }

      const { error } = await supabase
        .from('class_trials')
        .update(updatePayload)
        .eq('id', trial.id);

      if (error) throw error;

      setTrials((prev) =>
        prev.map((t) =>
          t.id === trial.id
            ? {
                ...t,
                status: newStatus,
                cost,
                ...(orderInfo
                  ? { order_code: orderInfo.orderCode, order_id: orderInfo.orderId }
                  : {}),
              }
            : t,
        ),
      );

      toast({
        title: 'อัปเดตสถานะแล้ว',
      });
      return true;
    } catch (error) {
      console.error('Error updating trial status:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเปลี่ยนสถานะได้',
        variant: 'destructive',
      });
      return false;
    }
  }, []);

  return {
    trials,
    isLoading,
    fetchTrials,
    createTrial,
    updateTrial,
    deleteTrial,
    updateTrialKanbanStatus,
  };
}
